import { BackButtonCWUI, ButtonUI } from '@next-order/next-order-components';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { AsYouType } from 'libphonenumber-js';
import { isNumberMobileAndValid } from 'helpers/phoneNumberHelper';
import { API_URL } from 'configuration/urls';
import OtpInput from 'react-otp-input';
import nextLoading from '../../assets/images/next-loading.svg';

const NO_OF_OTP_INPUTS = 6;

export default function MobileUpdateComponent({
  onBack,
  onSuccess,
  currentMobile,
  userDetails,
}) {
  const storeConfig = useSelector(selectStoreConfig);
  const { storeId, restaurantId, countryIdentifier } = storeConfig;

  const [step, setStep] = useState('mobile'); // 'mobile' or 'otp'
  const [mobileNumber, setMobileNumber] = useState('');
  const [displayMobileNumber, setDisplayMobileNumber] = useState('');
  const [validatedNumber, setValidatedNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [authCodeCounter, setAuthCodeCounter] = useState(0);
  const [isCodeResent, setIsCodeResent] = useState(false);

  const mobileNumberRef = useRef();
  const otpRef = useRef();

  useEffect(() => {
    if (step === 'mobile') {
      mobileNumberRef.current?.focus();
    } else if (step === 'otp') {
      otpRef?.current?.focusInput?.(0);
    }
  }, [step]);

  useEffect(() => {
    if (authCodeCounter > 0) {
      const timer = setTimeout(() => {
        setAuthCodeCounter(authCodeCounter - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setIsCodeResent(false);
    }
  }, [authCodeCounter]);

  useEffect(() => {
    if (verificationCode.length === NO_OF_OTP_INPUTS) {
      handleVerifyOTP();
    }
  }, [verificationCode]);

  const validateMobileNumber = (mobile) => {
    let numberAsUserType = new AsYouType(countryIdentifier).input(mobile);
    const { number, error } = isNumberMobileAndValid(
      numberAsUserType,
      countryIdentifier
    );

    setDisplayMobileNumber(numberAsUserType);
    setValidatedNumber(number || '');
    setErrors((prev) => ({ ...prev, mobileNumber: error }));
  };

  const handleMobileNumberChange = (e) => {
    const mobile = e.target.value;
    setMobileNumber(mobile);
    validateMobileNumber(mobile);
    // Clear errors when user starts typing
    if (errors.mobileNumber) {
      setErrors((prev) => ({ ...prev, mobileNumber: '' }));
    }
  };

  const handleMobileEnterPress = (e) => {
    if (e.keyCode === 13) {
      handleSendOTP();
    }
  };

  const handleSendOTP = async () => {
    if (!validatedNumber || errors.mobileNumber) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: 'Please enter a valid mobile number',
      }));
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const requestObj = {
        mobileNumber: validatedNumber,
        restaurantId,
        storeId,
        timeZone: storeConfig.timeZone,
      };

      const response = await fetch(`${API_URL}api/v1/sendMobileOTP`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestObj),
      });

      const responseData = await response.json();

      if (responseData?.errors?.length > 0) {
        setErrors({
          mobileNumber:
            responseData?.errors[0] || 'Failed to send OTP. Please try again.',
        });
      } else {
        setStep('otp');
        setAuthCodeCounter(30);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrors({ mobileNumber: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (verificationCode.length < 6) {
      setErrors((prev) => ({
        ...prev,
        verificationCode: 'Verification code must be 6 digits',
      }));
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const requestObj = {
        mobileNumber: validatedNumber,
        otp: verificationCode,
        userId: userDetails.userId,
        restaurantId,
        storeId,
      };

      const response = await fetch(`${API_URL}api/v1/verifyUpdatedMoblieOTP`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestObj),
      });

      const responseData = await response.json();

      if (responseData?.errors?.length > 0 || !responseData?.data) {
        setErrors({
          verificationCode:
            responseData?.errors[0] || 'Invalid verification code',
        });
        setVerificationCode('');
        otpRef?.current?.focusInput?.(0);
      } else {
        // Success - call the success callback with the new mobile number
        onSuccess(validatedNumber);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrors({
        verificationCode: 'Failed to verify OTP. Please try again.',
      });
      setVerificationCode('');
      otpRef?.current?.focusInput?.(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (authCodeCounter > 0) return;

    setIsLoading(true);
    try {
      const requestObj = {
        mobileNumber: validatedNumber,
        restaurantId,
        storeId,
      };

      const response = await fetch(`${API_URL}api/v1/resendOTP`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestObj),
      });

      const responseData = await response.json();

      if (responseData?.errors?.length === 0) {
        setIsCodeResent(true);
        setAuthCodeCounter(30);
        setVerificationCode('');
        otpRef?.current?.focusInput?.(0);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback(
    (code) => {
      setVerificationCode(code);
      // Clear error when user starts typing
      if (errors.verificationCode) {
        setErrors((prev) => ({ ...prev, verificationCode: '' }));
      }
    },
    [errors.verificationCode]
  );

  const handleBackClick = () => {
    if (step === 'otp') {
      setStep('mobile');
      setVerificationCode('');
      setErrors({});
    } else {
      onBack();
    }
  };

  if (step === 'mobile') {
    return (
      <div className='detailsConfirm mobileScr active'>
        <BackButtonCWUI onClick={handleBackClick} />
        <div className='detailsTitle' style={{ marginTop: 0 }}>
          Update Mobile Number
        </div>
        <div className='inputGroup'>
          <span>New Mobile Number</span>
          <input
            type='tel'
            id='mobileNumber'
            name='mobileNumber'
            ref={mobileNumberRef}
            className={errors.mobileNumber ? 'error' : ''}
            value={displayMobileNumber}
            onKeyDown={handleMobileEnterPress}
            onChange={handleMobileNumberChange}
            autoFocus
          />
          {errors.mobileNumber && (
            <div className='field-errors'>{errors.mobileNumber}</div>
          )}
        </div>

        <div className='inputGroup'>
          <div id='mobile-submit' />
        </div>

        <div
          className={`nextDetails${
            isLoading || errors.mobileNumber || !validatedNumber
              ? ' disabled'
              : ''
          }`}
          onClick={handleSendOTP}
        >
          {isLoading ? <img src={nextLoading} alt='' /> : 'Next'}
          {isLoading ? null : <i className='thickcon-Right-Chevron' />}
        </div>
      </div>
    );
  }

  return (
    <div className='detailsConfirm detailMobileVer active'>
      <BackButtonCWUI onClick={handleBackClick} />
      <div className='detailsTitle' style={{ marginTop: 0 }}>
        Verify your new mobile number
      </div>
      <div className='mobVerImp'>
        <span>Verification Code</span>
        <OtpInput
          ref={otpRef}
          value={verificationCode}
          onChange={handleChange}
          numInputs={NO_OF_OTP_INPUTS}
          isInputNum
          shouldAutoFocus
          containerStyle='verification-code-container'
          inputStyle={'verification-otp-input text-2xl text-bold'}
          focusStyle={'verification-otp-input-focus'}
          errorStyle={'verification-otp-input-error'}
          hasErrored={!!errors.verificationCode}
          isDisabled={isLoading}
          renderInput={(props) => <input {...props} />}
        />
        {errors.verificationCode && (
          <p className='text-red-600'>{errors.verificationCode}</p>
        )}

        <div
          className={`phoneRe active ${authCodeCounter ? ' disabled' : ''}`}
          onClick={handleResendOTP}
        >
          {isCodeResent ? 'Sent!' : 'Resend'}
        </div>

        {authCodeCounter > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <span>Please wait for {authCodeCounter} seconds</span>
          </div>
        )}
      </div>
    </div>
  );
}
